import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Drawer,
  FormControl,
  InputLabel,
  TextField,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import UndoIcon from '@mui/icons-material/Undo';

const btnStyle = {
  minWidth: '44px',
  width: '44px',
  height: '40px',
  p: 0,
};

const BulkKeywordReplacementDrawer = ({
  open,
  onClose,
  onCancel = null,
  onApply,
  onReplacedChange = null,
  defaultValues = {
    replaced: '',
    replacement: '',
  },
  disabled = false,
}) => {
  const [replaced, setReplaced] = useState('');
  const [replacement, setReplacement] = useState('');
  const [replacementError, setReplacementError] = useState('');
  const wasOpenRef = useRef(false);

  const { t } = useTranslation(['words_settings', 'promptbar']);

  const replacementRegex = /^[a-zа-яё0-9 ]*$/;

  useEffect(() => {
    // Инициализируем поля только в момент открытия Drawer,
    // чтобы изменения query-параметров во время ввода не сбрасывали второе поле.
    if (open && !wasOpenRef.current) {
      setReplaced(defaultValues?.replaced || '');
      setReplacement(defaultValues?.replacement || '');
      setReplacementError('');
    }

    wasOpenRef.current = open;
  }, [open, defaultValues?.replaced, defaultValues?.replacement]);

  const clearFields = () => {
    setReplaced('');
    setReplacement('');
    setReplacementError('');
  };

  const validateReplacement = value => {
    if (!value || replacementRegex.test(value)) {
      setReplacementError('');
      return true;
    }

    setReplacementError(t('promptbar:incorrectFormat'));
    return false;
  };

  const handleClose = () => {
    onClose();
  };

  const handleCancel = () => {
    clearFields();
    onCancel?.();
  };

  const handleSubmit = async () => {
    if (!validateReplacement(replacement)) {
      return;
    }

    const trimmedReplaced = replaced.trim();
    const trimmedReplacement = replacement.trim();

    const isSuccess = await onApply({
      replaced: trimmedReplaced,
      replacement: trimmedReplacement,
    });

    if (isSuccess) {
      setReplaced(trimmedReplacement);
      setReplacement('');
      setReplacementError('');
    }
  };

  const isReplaceDisabled =
    disabled ||
    !replaced.trim() ||
    !replacement.trim() ||
    Boolean(replacementError);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          top: 70,
          bottom: 'auto',
          height: 'auto',
          overflow: 'visible',
        },
      }}
    >
      <Box
        sx={{
          width: { xs: '100vw', sm: '700px' },
          display: 'flex',
          flexDirection: 'column',
        }}
        role="presentation"
      >
        <IconButton
          onClick={handleClose}
          size="small"
          aria-label={t('promptbar:close')}
          sx={{
            position: 'absolute',
            left: '-14px',
            top: '-14px',
            backgroundColor: 'var(--color-bg-overlay)',
            boxShadow: 1,
            '&:hover': {
              backgroundColor: 'background.paper',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <Box
          component="form"
          autoComplete="off"
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            padding: '3rem 1.5rem 1.5rem',
          }}
        >
          {/* Поле "Заменить" */}
          <FormControl
            fullWidth
            sx={{
              flex: 1,
              minWidth: 0,
            }}
          >
            <InputLabel htmlFor="bulk-replaced-input" shrink={false}>
              {t('words_settings:bulkReplace.replacedLabel')}
            </InputLabel>
            <TextField
              id="bulk-replaced-input"
              value={replaced}
              onChange={event => {
                const { value } = event.target;
                setReplaced(value);
                onReplacedChange?.(value);
              }}
              disabled={disabled}
            />
          </FormControl>

          {/* Поле "Замена" */}
          <FormControl
            fullWidth
            sx={{
              flex: 1,
              minWidth: 0,
            }}
          >
            <InputLabel htmlFor="bulk-replacement-input" shrink={false}>
              {t('words_settings:bulkReplace.replacementLabel')}
            </InputLabel>
            <TextField
              id="bulk-replacement-input"
              value={replacement}
              onChange={event => {
                const { value } = event.target;
                setReplacement(value);
                validateReplacement(value);
              }}
              onBlur={event => validateReplacement(event.target.value)}
              error={Boolean(replacementError)}
              helperText={replacementError}
              disabled={disabled}
            />
          </FormControl>
          <Button
            type="button"
            variant="contained"
            onClick={handleSubmit}
            disabled={isReplaceDisabled}
            aria-label={t('promptbar:save')}
            title={t('promptbar:save')}
            sx={btnStyle}
          >
            <CheckIcon />
          </Button>

          <Button
            type="button"
            color="secondary"
            variant="contained"
            onClick={handleCancel}
            disabled={disabled}
            aria-label={t('promptbar:cancel')}
            title={t('promptbar:cancel')}
            sx={btnStyle}
          >
            <UndoIcon />
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

BulkKeywordReplacementDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  onApply: PropTypes.func.isRequired,
  onReplacedChange: PropTypes.func,
  disabled: PropTypes.bool,
  defaultValues: PropTypes.shape({
    replaced: PropTypes.string,
    replacement: PropTypes.string,
  }),
};

export default BulkKeywordReplacementDrawer;
